import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBrandManager } from '@/hooks/useBrandManager';
import { toast } from 'sonner';

interface QuotaUsage {
  art_usage: number;
  credits_usage: number;
  current_month_start: string;
}

interface QuotaLimits {
  art_quota: number;
  credits_quota: number;
  quotas_last_updated: string;
}

interface QuotaStatus extends QuotaUsage, QuotaLimits {
  art_remaining: number;
  credits_remaining: number;
  art_percentage: number;
  credits_percentage: number;
  can_generate: boolean;
}

export const useQuotaManagement = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const [quotaStatus, setQuotaStatus] = useState<QuotaStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentBrandId } = useBrandManager();

  const fetchQuotaStatus = async () => {
    if (!currentBrandId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Get quota limits from brand_data
      const { data: brandData, error: brandError } = await supabase
        .from('brand_data')
        .select('art_quota, credits_quota, quotas_last_updated')
        .eq('brand_id', currentBrandId)
        .maybeSingle();

      if (brandError) {
        console.error('Error fetching brand quota limits:', brandError);
        return;
      }

      // If no brand_data exists yet, quotaStatus will be null
      if (!brandData) {
        setQuotaStatus(null);
        return;
      }

      // Get current usage from database function
      const { data: usageData, error: usageError } = await supabase
        .rpc('get_brand_quota_usage', { brand_id_param: currentBrandId });

      if (usageError) {
        console.error('Error fetching quota usage:', usageError);
        return;
      }

      // Type the usage data properly - handle null/undefined data
      if (!usageData) {
        console.error('No usage data returned');
        return;
      }
      const usage = usageData as unknown as QuotaUsage;
      
      // Calculate quota status
      const art_remaining = Math.max(0, (brandData.art_quota || 50) - (usage.art_usage || 0));
      const credits_remaining = Math.max(0, (brandData.credits_quota || 1000) - (usage.credits_usage || 0));
      const art_percentage = ((usage.art_usage || 0) / (brandData.art_quota || 50)) * 100;
      const credits_percentage = ((usage.credits_usage || 0) / (brandData.credits_quota || 1000)) * 100;

      const status: QuotaStatus = {
        art_usage: usage.art_usage || 0,
        credits_usage: usage.credits_usage || 0,
        current_month_start: usage.current_month_start,
        art_quota: brandData.art_quota || 50,
        credits_quota: brandData.credits_quota || 1000,
        quotas_last_updated: brandData.quotas_last_updated,
        art_remaining,
        credits_remaining,
        art_percentage,
        credits_percentage,
        can_generate: art_remaining > 0 && credits_remaining > 0,
      };

      setQuotaStatus(status);
    } catch (error) {
      console.error('Error in fetchQuotaStatus:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkQuotaBeforeGeneration = async (): Promise<boolean> => {
    if (!quotaStatus) {
      await fetchQuotaStatus();
      return false;
    }

    if (quotaStatus.art_remaining <= 0) {
      toast.error('Art generation quota exceeded', {
        description: `You have reached your monthly limit of ${quotaStatus.art_quota} art generations.`,
      });
      return false;
    }

    if (quotaStatus.credits_remaining <= 0) {
      toast.error('Credits quota exceeded', {
        description: `You have reached your monthly limit of ${quotaStatus.credits_quota} credits.`,
      });
      return false;
    }

    return true;
  };

  const updateQuotas = async (artQuota: number, creditsQuota: number): Promise<boolean> => {
    if (!currentBrandId) return false;

    try {
      const { data, error } = await supabase
        .rpc('upsert_brand_quota', {
          brand_id_param: currentBrandId,
          art_quota_param: artQuota,
          credits_quota_param: creditsQuota,
        });

      if (error) {
        console.error('Error updating quotas:', error);
        toast.error('Failed to update quotas', {
          description: error.message,
        });
        return false;
      }

      toast.success('Quotas updated successfully');
      await fetchQuotaStatus();
      return true;
    } catch (error) {
      console.error('Error in updateQuotas:', error);
      toast.error('Failed to update quotas');
      return false;
    }
  };

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    fetchQuotaStatus();
  }, [currentBrandId, enabled]);

  return {
    quotaStatus,
    isLoading,
    checkQuotaBeforeGeneration,
    updateQuotas,
    refreshQuotaStatus: fetchQuotaStatus,
  };
};